import { ChangeEvent, FC, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import { Box, Drawer, IconButton, InputAdornment, Link, Stack, TextField, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import MainNav from "../components/mainNav";
import logo from '../assets/new-tkfm-transparent.png';
import SocialNav from "../components/socialNav";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useQuickFilterPortal from "../hooks/useQuickFilterPortal";
import useQueryStringParams from "../hooks/useQueryStringParams";
import useMediaQueryStops from "../hooks/useMediaQueryStops";
import { useLicenses } from "../contexts/licenseContext";
import LicenseCart from "../components/licenseCart";

const RootScreen: FC = () => {
	const theme = useTheme();
	const { getQueryParam, navigateWithParam } = useQueryStringParams();
	const portal = useQuickFilterPortal();
	const { isXs, isSm } = useMediaQueryStops();
	const { songsInCart } = useLicenses();

	const searchPlaceholder = useMemo(() => {
		if (isXs) return 'Search tunes';
		if (isSm) return 'Search tunes, albums, and tags';
		
		return 'Search by title, album, genre, mood, or BPM';
	}, [isXs, isSm]);

	const [filterText, setFilterText] = useState('');
	const quickFilterRef = useRef<HTMLInputElement | null>(null);

	useEffect(() => {
		const queryText = getQueryParam('q');

		if (queryText) {
			setFilterText(queryText);
			portal.setFilterText(queryText);
		}
	}, [portal, getQueryParam]);

	const handleFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
		setFilterText(event.target.value);
	};

	const handleFilterKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter' && filterText && filterText.trim()) {
			portal.setFilterText(filterText);
			navigateWithParam('/tunes', 'q', filterText);
		}
	};

	const handleFilterClear = () => {
		setFilterText('');
		portal.setFilterText('');
		quickFilterRef.current?.focus();
	};

	return (
		<>
			<Grid id="siteRoot" container spacing={2} sx={{ flex: '1 1 auto', backgroundColor: theme.palette.background.default }}>
				<Grid
					size={{ xs: 12, sm: 12, md: 12, lg: 10, xl: 8 }}
					offset={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 2 }}
					sx={{
						position: 'relative'
					}}
				>
					<Stack
						direction="row"
						spacing={4}
						sx={{
							position: 'fixed',
							top: 0,
							right: 0,
							left: 0,
							alignItems: "center",
							height: 57.5,
							mx: { sx: 0, sm: 0, md: 0, lg: (1 / 12) * 100 + '%', xl: (2 / 12) * 100 + '%' },
							backgroundColor: theme.palette.background.default,
							px: { xs: 0, sm: 0, md: 4, lg: 0, xl: 0 },
							py: 1,
							zIndex: 2,
							'::before': {
								content: '""',
								position: 'fixed',
								top: -200,
								right: 0,
								left: 0,
								height: 200,
								backgroundColor: theme.palette.background.default,
								zIndex: 2
							}
						}}
					>
						<Box sx={{ width: 160, pl: '40px' }}>
							<Link href="/">
								<Box component="img" src={logo} height={38} />
							</Link>
						</Box>
						{portal.isDataGridScreen && (
							<Box id="search-container" sx={{ flex: '1 1 auto', width: 600 }} />
						)}
						{!portal.isDataGridScreen && (
							<TextField
								inputRef={quickFilterRef}
								variant="outlined"
								size="small"
								sx={{ flex: '1 1 auto', width: 600, height: 36, padding: 0 }}
								slotProps={{
									input: {
										startAdornment: (
											<InputAdornment position="start">
												<FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'search' }} style={{ color: theme.palette.text.secondary }} />
											</InputAdornment>
										),
										endAdornment: filterText && (
											<InputAdornment position="end">
												<IconButton
													size="small"
													sx={{ width: 28, height: 28 }}
													onClick={handleFilterClear}
												>
													<FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'times' }} style={{ color: theme.palette.text.secondary }} />
												</IconButton>
											</InputAdornment>
										)
									}
								}}
								placeholder={searchPlaceholder}
								value={filterText}
								onChange={handleFilterChange}
								onKeyDown={handleFilterKeyDown}
							/>
						)}
						<Box sx={{ display: { xs: 'block', sm: 'block', md: 'none', lg: 'none', xl: 'none' }, pr: 5 }}>
							<MainNav mode="mobile" />
						</Box>
					</Stack>
					<Box mt="73.5px" px={{ xs: 2, sm: 4, md: 4, lg: 0, xl: 0 }}>
						<Stack
							sx={{
								position: 'fixed',
								display: { xs: 'none', sm: 'none', md: 'flex', lg: 'flex', xl: 'flex' }
							}}
						>
							<MainNav />
							<SocialNav />
						</Stack>
						<Box
							component="main"
							sx={{
								ml: { xs: 0, sm: 0, md: '232px', lg: '232px', xl: '232px' }
							}}
						>
							<Outlet />
						</Box>
					</Box>
				</Grid>
			</Grid>
			<Drawer
				variant="persistent"
				anchor="right"
				open={!!songsInCart.length}
				PaperProps={{
					elevation: 2,
					sx: { zIndex: 1400 }
				}}
				sx={{
					flex: songsInCart.length ? '0 0 340px' : '0 0 0px'
				}}
			>
				<Stack
					spacing={2}
					sx={{
						width: 340,
						height: '100%'
					}}
				>
					<LicenseCart />
				</Stack>
			</Drawer>
		</>
	);
};

export default RootScreen;
